// src/zlecenia/ZlecenieKosztySprzedazPodglad.js
import React, { useEffect, useMemo, useState, forwardRef, useImperativeHandle } from "react";
import axios from "axios";
import "./ZleceniaStyle/SzczegolyZleceniaTabela.css";

const API_URL = process.env.REACT_APP_API_URL;

// === utils ===
function toNum(v) {
  if (v === "" || v == null) return NaN;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
}
function round2(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.round((x + Number.EPSILON) * 100) / 100;
}
function fmt2(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "-";
  return x.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmt3(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "-";
  return x.toLocaleString("pl-PL", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

/** Buduje sekcje pdfmake (tabele kosztów i sprzedaży) */
export function buildPdfContentFromKS(joinedRows, sumsKoszt, sumsSprzedaz) {
  const kosztyHeader = [
    { text: "L.P.", bold: true, fillColor: "#e6f0ff", alignment: "center", noWrap: true },
    { text: "Nazwa", bold: true, fillColor: "#e6f0ff" },
    { text: "Ilość", bold: true, fillColor: "#e6f0ff", alignment: "right", noWrap: true },
    { text: "J.M.", bold: true, fillColor: "#e6f0ff", alignment: "center", noWrap: true },
    { text: "Koszt netto j.", bold: true, fillColor: "#e6f0ff", alignment: "right", noWrap: true },
    { text: "VAT %", bold: true, fillColor: "#e6f0ff", alignment: "right", noWrap: true },
    { text: "VAT j.", bold: true, fillColor: "#e6f0ff", alignment: "right", noWrap: true },
    { text: "Razem netto", bold: true, fillColor: "#e6f0ff", alignment: "right", noWrap: true },
    { text: "Razem VAT", bold: true, fillColor: "#e6f0ff", alignment: "right", noWrap: true },
    { text: "Razem brutto", bold: true, fillColor: "#e6f0ff", alignment: "right", noWrap: true }
  ];

  const kosztyBody = [
    kosztyHeader,
    ...joinedRows.map((r, i) => ([
      { text: String(i + 1), alignment: "center", noWrap: true },
      r.nazwa_at_sale || "",
      { text: fmt3(r.ilosc), alignment: "right", noWrap: true },
      { text: r.jednostka_symbol_at_sale || "", alignment: "center", noWrap: true },
      { text: fmt2(r.koszt_jednostkowy_netto_at_sale), alignment: "right", noWrap: true },
      { text: fmt2(r.vat_proc_at_sale), alignment: "right", noWrap: true },
      { text: fmt2(r.kwota_vat_unit), alignment: "right", noWrap: true },
      { text: fmt2(r.razem_netto_koszt), alignment: "right", noWrap: true },
      { text: fmt2(r.razem_vat_koszt), alignment: "right", noWrap: true },
      { text: fmt2(r.razem_brutto_koszt), alignment: "right", noWrap: true }
    ])),
    [
      { text: "Suma", colSpan: 7, alignment: "right", bold: true }, {}, {}, {}, {}, {}, {},
      { text: fmt2(sumsKoszt.netto), alignment: "right", bold: true, noWrap: true },
      { text: fmt2(sumsKoszt.vat), alignment: "right", bold: true, noWrap: true },
      { text: fmt2(sumsKoszt.brutto), alignment: "right", bold: true, noWrap: true }
    ]
  ];

  const sprzedazHeader = [
    { text: "L.P.", bold: true, fillColor: "#e6f0ff", alignment: "center", noWrap: true },
    { text: "Nazwa", bold: true, fillColor: "#e6f0ff" },
    { text: "Ilość", bold: true, fillColor: "#e6f0ff", alignment: "right", noWrap: true },
    { text: "J.M.", bold: true, fillColor: "#e6f0ff", alignment: "center", noWrap: true },
    { text: "Cena netto j.", bold: true, fillColor: "#e6f0ff", alignment: "right", noWrap: true },
    { text: "VAT %", bold: true, fillColor: "#e6f0ff", alignment: "right", noWrap: true },
    { text: "VAT j.", bold: true, fillColor: "#e6f0ff", alignment: "right", noWrap: true },
    { text: "Cena brutto j.", bold: true, fillColor: "#e6f0ff", alignment: "right", noWrap: true },
    { text: "Razem netto", bold: true, fillColor: "#e6f0ff", alignment: "right", noWrap: true },
    { text: "Razem VAT", bold: true, fillColor: "#e6f0ff", alignment: "right", noWrap: true },
    { text: "Razem brutto", bold: true, fillColor: "#e6f0ff", alignment: "right", noWrap: true }
  ];

  const sprzedazRows = joinedRows
    .filter(r => r.in_sprzedaz && r._sprz)
    .map((r, i) => ([
      { text: String(i + 1), alignment: "center", noWrap: true },
      r.nazwa_at_sale || "",
      { text: fmt3(r.ilosc), alignment: "right", noWrap: true },
      { text: r.jednostka_symbol_at_sale || "", alignment: "center", noWrap: true },
      { text: fmt2(r.cena_netto), alignment: "right", noWrap: true },
      { text: fmt2(r.vat_proc_at_sale), alignment: "right", noWrap: true },
      { text: fmt2(r.sprz_vat_unit), alignment: "right", noWrap: true },
      { text: fmt2(r.cena_brutto_unit), alignment: "right", noWrap: true },
      { text: fmt2(r.sprz_razem_netto), alignment: "right", noWrap: true },
      { text: fmt2(r.sprz_razem_vat), alignment: "right", noWrap: true },
      { text: fmt2(r.sprz_razem_brutto), alignment: "right", noWrap: true }
    ]));

  const sprzedazBody = [
    sprzedazHeader,
    ...sprzedazRows,
    [
      { text: "Suma", colSpan: 8, alignment: "right", bold: true }, {}, {}, {}, {}, {}, {}, {},
      { text: fmt2(sumsSprzedaz.netto), alignment: "right", bold: true, noWrap: true },
      { text: fmt2(sumsSprzedaz.vat), alignment: "right", bold: true, noWrap: true },
      { text: fmt2(sumsSprzedaz.brutto), alignment: "right", bold: true, noWrap: true }
    ]
  ];

  return [
    { text: "Pozycje kosztowe", style: "section", margin: [0, 10, 0, 5] },
    {
      table: {
        widths: [20, "*", 38, 28, 60, 32, 55, 65, 60, 70],
        body: kosztyBody
      },
      style: "compact",
      layout: {
        fillColor: (row) => (row === 0 ? '#e6f0ff' : null),
        paddingLeft: () => 2, paddingRight: () => 2,
        paddingTop: () => 2,  paddingBottom: () => 2,
        hLineWidth: () => 0.3, vLineWidth: () => 0.3
      },
      dontBreakRows: true,
      keepWithHeaderRows: 1,
      margin: [0, 0, 0, 8]
    },

    { text: "Pozycje sprzedażowe", style: "section", margin: [0, 10, 0, 5] },
    {
      table: {
        widths: [20, "*", 38, 28, 60, 32, 60, 68, 65, 60, 70],
        body: sprzedazBody
      },
      style: "compact",
      layout: {
        fillColor: (row) => (row === 0 ? '#e6f0ff' : null),
        paddingLeft: () => 2, paddingRight: () => 2,
        paddingTop: () => 2,  paddingBottom: () => 2,
        hLineWidth: () => 0.3, vLineWidth: () => 0.3
      },
      dontBreakRows: true,
      keepWithHeaderRows: 1,
      margin: [0, 0, 0, 8]
    }
  ];
}

const ZlecenieKosztySprzedazPodglad = forwardRef(function ZKSPodglad({ zlecenieId }, ref) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [koszty, setKoszty] = useState([]);
  const [sprzedaz, setSprzedaz] = useState([]);

  useEffect(() => {
    let ignore = false;
    const token = localStorage.getItem("token");

    async function load() {
      setLoading(true);
      setError("");

      try {
        // koszty
        const kRes = await axios.get(
          `${API_URL}/koszty-pozycje/zlecenia/${zlecenieId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const k = Array.isArray(kRes.data) ? kRes.data : [];

        // sprzedaż (JOIN po backendzie)
        const sRes = await axios.get(
          `${API_URL}/sprzedaz-pozycje/zlecenia/${zlecenieId}`,
          { params: { calc: "true", hidePrices: "false" }, headers: { Authorization: `Bearer ${token}` } }
        );
        const s = Array.isArray(sRes.data) ? sRes.data : [];

        if (!ignore) {
          setKoszty(k);
          setSprzedaz(s);
        }
      } catch (e) {
        console.error("Błąd pobierania pozycji K/S:", e);
        if (!ignore) setError("Nie udało się pobrać pozycji kosztów/sprzedaży.");
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    if (zlecenieId) load();
    return () => { ignore = true; };
  }, [zlecenieId]);

  // Mapa sprzedaży po id_koszt
  const sprzByKoszt = useMemo(() => {
    const m = new Map();
    for (const s of sprzedaz || []) {
      if (s && (s.id_koszt || s.idkoszt || s.id_kosztu)) {
        m.set(s.id_koszt ?? s.idkoszt ?? s.id_kosztu, s);
      }
    }
    return m;
  }, [sprzedaz]);

  // Połącz i przelicz (wspólne dla PDF i podglądu)
  const joined = useMemo(() => {
    return (koszty || []).map((k) => {
      const ilosc = toNum(k.ilosc);
      const koszt = toNum(k.koszt_jednostkowy_netto_at_sale);
      const vat = toNum(k.vat_proc_at_sale);
      const sprz = sprzByKoszt.get(k.id_koszt) || null;

      // KOSZT
      const kwota_vat_unit = Number.isFinite(koszt) && Number.isFinite(vat) ? round2(koszt * (vat / 100)) : 0;
      const razem_netto_koszt = Number.isFinite(koszt) && Number.isFinite(ilosc) ? round2(koszt * ilosc) : 0;
      const razem_vat_koszt = Number.isFinite(ilosc) ? round2(kwota_vat_unit * ilosc) : 0;
      const razem_brutto_koszt = Number.isFinite(vat) ? round2(razem_netto_koszt * (1 + vat / 100)) : 0;

      // SPRZEDAŻ
      const cena_netto = sprz ? toNum(sprz.cena_netto_at_sale) : NaN;
      const cena_brutto_unit =
        Number.isFinite(cena_netto) && Number.isFinite(vat)
          ? round2(cena_netto * (1 + vat / 100))
          : 0;
      const sprz_vat_unit =
        Number.isFinite(cena_netto) && Number.isFinite(vat)
          ? round2(cena_netto * (vat / 100))
          : 0;
      const sprz_razem_netto =
        Number.isFinite(cena_netto) && Number.isFinite(ilosc) ? round2(cena_netto * ilosc) : 0;
      const sprz_razem_vat =
        Number.isFinite(vat) ? round2(sprz_razem_netto * (vat / 100)) : 0;
      const sprz_razem_brutto =
        Number.isFinite(sprz_razem_netto) ? round2(sprz_razem_netto + sprz_razem_vat) : 0;

      return {
        ...k,
        _sprz: sprz,
        kwota_vat_unit,
        razem_netto_koszt,
        razem_vat_koszt,
        razem_brutto_koszt,
        cena_netto,
        cena_brutto_unit,
        sprz_vat_unit,
        sprz_razem_netto,
        sprz_razem_vat,
        sprz_razem_brutto
      };
    });
  }, [koszty, sprzByKoszt]);

  const sumsKoszt = useMemo(() => {
    return joined.reduce(
      (acc, r) => {
        acc.netto += Number(r.razem_netto_koszt || 0);
        acc.vat   += Number(r.razem_vat_koszt   || 0);
        acc.brutto+= Number(r.razem_brutto_koszt|| 0);
        return acc;
      }, { netto: 0, vat: 0, brutto: 0 }
    );
  }, [joined]);

  const sumsSprzedaz = useMemo(() => {
    return joined
      .filter(r => r.in_sprzedaz && r._sprz)
      .reduce((acc, r) => {
        acc.netto += Number(r.sprz_razem_netto || 0);
        acc.vat   += Number(r.sprz_razem_vat   || 0);
        acc.brutto+= Number(r.sprz_razem_brutto|| 0);
        return acc;
      }, { netto: 0, vat: 0, brutto: 0 });
  }, [joined]);

  // API dla rodzica (PDF)
  useImperativeHandle(ref, () => ({
    getPdfContent: () => buildPdfContentFromKS(joined, sumsKoszt, sumsSprzedaz),
    getRawData: () => ({ joinedRows: joined, sumsKoszt, sumsSprzedaz })
  }), [joined, sumsKoszt, sumsSprzedaz]);

  if (loading) return <div>Ładowanie pozycji…</div>;
  if (error)   return <div className="error">{error}</div>;

  // ====== RENDER PODGLĄDU W MODALU ======
  return (
    <div className="w-full space-y-8">
      {/* KOSZTY */}
      <div className="kst-card rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="px-3 py-2 font-semibold">Pozycje kosztowe</div>
        <div className="overflow-auto">
          <table className="costs-table w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr className="bg-gray-100 text-gray-600">
                <th className="text-left p-2 border-b">L.P.</th>
                <th className="text-left p-2 border-b">Nazwa</th>
                <th className="text-right p-2 border-b">Ilość</th>
                <th className="text-center p-2 border-b">J.M.</th>
                <th className="text-right p-2 border-b">Koszt netto j.</th>
                <th className="text-right p-2 border-b">VAT %</th>
                <th className="text-right p-2 border-b">VAT j.</th>
                <th className="text-right p-2 border-b">Razem netto</th>
                <th className="text-right p-2 border-b">Razem VAT</th>
                <th className="text-right p-2 border-b">Razem brutto</th>
              </tr>
            </thead>
            <tbody>
              {joined.map((r, idx) => (
                <tr key={`k-${r.id_koszt || idx}`} className="hover:bg-gray-50">
                  <td className="p-2 border-b">{idx + 1}</td>
                  <td className="p-2 border-b">{r.nazwa_at_sale || ""}</td>
                  <td className="p-2 border-b text-right">{fmt3(r.ilosc)}</td>
                  <td className="p-2 border-b text-center">{r.jednostka_symbol_at_sale || ""}</td>
                  <td className="p-2 border-b text-right">{fmt2(r.koszt_jednostkowy_netto_at_sale)}</td>
                  <td className="p-2 border-b text-right">{fmt2(r.vat_proc_at_sale)}</td>
                  <td className="p-2 border-b text-right">{fmt2(r.kwota_vat_unit)}</td>
                  <td className="p-2 border-b text-right">{fmt2(r.razem_netto_koszt)}</td>
                  <td className="p-2 border-b text-right">{fmt2(r.razem_vat_koszt)}</td>
                  <td className="p-2 border-b text-right">{fmt2(r.razem_brutto_koszt)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50">
                <td className="p-2 border-t text-gray-700" colSpan={7}>Suma</td>
                <td className="p-2 border-t text-right">{fmt2(sumsKoszt.netto)}</td>
                <td className="p-2 border-t text-right">{fmt2(sumsKoszt.vat)}</td>
                <td className="p-2 border-t text-right">{fmt2(sumsKoszt.brutto)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* SPRZEDAŻ */}
      <div className="kst-card rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="px-3 py-2 font-semibold">Pozycje sprzedażowe</div>
        <div className="overflow-auto">
          <table className="costs-table w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr className="bg-gray-100 text-gray-600">
                <th className="text-left p-2 border-b">L.P.</th>
                <th className="text-left p-2 border-b">Nazwa</th>
                <th className="text-right p-2 border-b">Ilość</th>
                <th className="text-center p-2 border-b">J.M.</th>
                <th className="text-right p-2 border-b">Cena netto j.</th>
                <th className="text-right p-2 border-b">VAT %</th>
                <th className="text-right p-2 border-b">VAT j.</th>
                <th className="text-right p-2 border-b">Cena brutto j.</th>
                <th className="text-right p-2 border-b">Razem netto</th>
                <th className="text-right p-2 border-b">Razem VAT</th>
                <th className="text-right p-2 border-b">Razem brutto</th>
              </tr>
            </thead>
            <tbody>
              {joined.filter(r => r.in_sprzedaz && r._sprz).map((r, idx) => (
                <tr key={`s-${r.id_koszt || idx}`} className="hover:bg-gray-50">
                  <td className="p-2 border-b">{idx + 1}</td>
                  <td className="p-2 border-b">{r.nazwa_at_sale || ""}</td>
                  <td className="p-2 border-b text-right">{fmt3(r.ilosc)}</td>
                  <td className="p-2 border-b text-center">{r.jednostka_symbol_at_sale || ""}</td>
                  <td className="p-2 border-b text-right">{fmt2(r.cena_netto)}</td>
                  <td className="p-2 border-b text-right">{fmt2(r.vat_proc_at_sale)}</td>
                  <td className="p-2 border-b text-right">{fmt2(r.sprz_vat_unit)}</td>
                  <td className="p-2 border-b text-right">{fmt2(r.cena_brutto_unit)}</td>
                  <td className="p-2 border-b text-right">{fmt2(r.sprz_razem_netto)}</td>
                  <td className="p-2 border-b text-right">{fmt2(r.sprz_razem_vat)}</td>
                  <td className="p-2 border-b text-right">{fmt2(r.sprz_razem_brutto)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50">
                <td className="p-2 border-t text-gray-700" colSpan={8}>Suma</td>
                <td className="p-2 border-t text-right">{fmt2(sumsSprzedaz.netto)}</td>
                <td className="p-2 border-t text-right">{fmt2(sumsSprzedaz.vat)}</td>
                <td className="p-2 border-t text-right">{fmt2(sumsSprzedaz.brutto)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
});

export default ZlecenieKosztySprzedazPodglad;
